import {
    AfterViewChecked,
    ChangeDetectorRef,
    Component,
    DOCUMENT,
    ElementRef,
    Inject,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { Subject, Subscription, timer } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { BillingPortalService, PaymentStatusDto } from '@api-client';

import { PermissionService } from '../../shared/services/permission/permission.service';
import { BillingLockService } from './billing-lock.service';
import { AnnouncementsService } from '../announcements/announcements.service';
import { ANNOUNCEMENT_KEYS, PREVIEW_CAMPAIGN } from '../announcements/announcement.model';
import { BILLING_EMAIL, isReminderStatus, reminderMessage } from './payment-popup.schedule';
import { logoutRequest } from '../../auth/auth-mode';

export type PaymentPopupMode = 'none' | 'reminder' | 'locked';

/** Admin Panel → Subscription (ZEY-3126). */
const STATEMENT_URL = '/subscription';

/**
 * Blocking billing overlay for the shell (ZEY-3046). It sits above every micro-UI iframe, Kendo
 * dialog and the menu header, blurs the page, and takes all pointer and keyboard input.
 *
 * - `reminder` (Tier2, subscription expired): shown only while the server's PaymentReminder
 *   announcement is due for this user (ZEY-3181). It counts down the server's `minDisplaySeconds`
 *   (the billing policy's `popupMinSeconds`), then closes itself and acknowledges on the server,
 *   which holds it back for `popupEveryMinutes`. Until that acknowledgement lands, every reload
 *   shows it again with a fresh countdown. Nothing is kept in the browser, and the popup never
 *   opens before the server has answered, so it can't flash open and shut at load.
 *   ESC and backdrop clicks do nothing. It never shows the amount due (ZEY-3071).
 *   A test preview (/recalculation → Banner Test) is the same server announcement under the campaign
 *   "preview", sent to that user only; its payload stands in for the payment status.
 * - `locked` (any API answered 402 `billing_locked`): "Account suspended", no way to close it —
 *   only pay / contact billing or sign out.
 *
 * Tier1 (before expiry) shows nothing: that warning goes out by email.
 */
@Component({
    selector: 'app-payment-popup',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './payment-popup.component.html',
    styleUrls: ['./payment-popup.component.scss'],
})
export class PaymentPopupComponent implements OnInit, OnChanges, AfterViewChecked, OnDestroy {
    @Input() paymentStatus: PaymentStatusDto | null | undefined;

    @ViewChild('dialog') dialogRef?: ElementRef<HTMLElement>;
    @ViewChild('overlay') overlayRef?: ElementRef<HTMLElement>;

    mode: PaymentPopupMode = 'none';
    secondsLeft = 0;
    isLoadingPortal = false;
    portalError = false;
    /** Body text, computed when the popup opens so it doesn't change while it is on screen. */
    message = '';
    /** The reminder links to Admin Panel → Subscription when this user's menu has it (ZEY-3126). */
    canViewStatement = false;

    private readonly destroy$ = new Subject<void>();
    private countdownSub?: Subscription;
    private needsFocus = false;
    private inerted: HTMLElement[] = [];
    private bodyObserver?: MutationObserver;

    private readonly onVisibility = () => {
        // A laptop waking from sleep may be past the reminder's next showing: ask the server again.
        if (this.document.visibilityState === 'visible') this.zone.run(() => void this.announcements.refresh());
    };
    private readonly onKeydownCapture = (e: KeyboardEvent) => this.trapKeydown(e);
    private readonly onFocusInCapture = (e: FocusEvent) => this.trapFocus(e);

    constructor(
        private portalSvc: BillingPortalService,
        private billingLock: BillingLockService,
        private msal: MsalService,
        private permissions: PermissionService,
        private announcements: AnnouncementsService,
        private router: Router,
        private zone: NgZone,
        private cdr: ChangeDetectorRef,
        @Inject(DOCUMENT) private document: Document,
    ) {}

    ngOnInit(): void {
        this.billingLock.locked$.pipe(takeUntil(this.destroy$)).subscribe(() => this.evaluate());
        // ZEY-3181: the server decides when the reminder is owed; re-check whenever its list changes.
        this.announcements.items$.pipe(takeUntil(this.destroy$)).subscribe(() => this.evaluate());
        this.document.addEventListener('visibilitychange', this.onVisibility);
    }

    ngOnChanges(): void {
        this.evaluate();
    }

    ngAfterViewChecked(): void {
        if (this.needsFocus && this.dialogRef && this.overlayRef) {
            this.needsFocus = false;
            this.portalToBody(this.overlayRef.nativeElement);
            this.focusDialog();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.document.removeEventListener('visibilitychange', this.onVisibility);
        const overlay = this.overlayRef?.nativeElement;
        this.close();
        overlay?.remove(); // it lives under <body>, outside this component's DOM
    }

    // ── state ──────────────────────────────────────────────────────────────

    get isStripe(): boolean {
        return this.status()?.payAction?.kind === 'Stripe';
    }

    get continueLabel(): string {
        return this.secondsLeft > 0 ? `Closes in ${this.secondsLeft}s` : 'Continue';
    }

    /** Re-reads the status, the server's reminder and the lock state and opens/closes the overlay accordingly. */
    evaluate(): void {
        if (this.billingLock.locked) {
            if (this.mode !== 'locked') this.open('locked');
            return;
        }
        if (!isReminderStatus(this.status())) {
            if (this.mode !== 'none') this.close();
            return;
        }
        const due = this.ackDue();
        if (this.mode === 'reminder') {
            // Already showing: never restart the countdown. Close only if the server no longer owes it
            // (acknowledged in another tab).
            if (!due) this.close();
            return;
        }
        if (due) this.open('reminder');
    }

    onContinue(): void {
        if (this.mode !== 'reminder' || this.secondsLeft > 0) return;
        this.acknowledge();
        this.close();
    }

    onSignOut(): void {
        this.msal.logout(logoutRequest(this.msal.instance));
    }

    /**
     * Opens the subscription statement (ZEY-3126). Counts as acknowledging the reminder, as
     * Continue does, so it doesn't come straight back over the statement.
     */
    onViewStatement(): void {
        if (this.mode !== 'reminder' || !this.canViewStatement) return;
        this.acknowledge();
        this.close();
        void this.router.navigateByUrl(STATEMENT_URL);
    }

    onPay(): void {
        const action = this.status()?.payAction;

        if (action?.kind === 'Stripe') {
            this.isLoadingPortal = true;
            this.portalError = false;
            this.portalSvc.meBillingPortalSessionPost().subscribe({
                next: (r) => {
                    this.isLoadingPortal = false;
                    this.cdr.markForCheck();
                    if (r?.url) {
                        this.navigate(r.url);
                    } else {
                        this.portalError = true;
                    }
                },
                error: () => {
                    this.isLoadingPortal = false;
                    this.portalError = true;
                    this.cdr.markForCheck();
                },
            });
            return;
        }

        // Email payers, and the suspended screen when the settings call itself was refused.
        this.navigate(action?.kind === 'Email' && action.url ? action.url : `mailto:${BILLING_EMAIL}`);
    }

    // ── seams (overridden in specs) ────────────────────────────────────────

    protected now(): number {
        return Date.now();
    }

    // window.location.assign can't be spied in Karma/Chrome (unforgeable exotic object).
    protected navigate(url: string | undefined): void {
        if (url) window.location.assign(url);
    }

    // ── internals ──────────────────────────────────────────────────────────

    private reminder() {
        return this.announcements.current(ANNOUNCEMENT_KEYS.paymentReminder);
    }

    /** The tenant's payment status, or for a test preview the sample status it was started with. */
    private status(): PaymentStatusDto | null | undefined {
        const reminder = this.reminder();
        if (reminder?.campaignKey === PREVIEW_CAMPAIGN && reminder.payload) {
            try {
                return JSON.parse(reminder.payload) as PaymentStatusDto;
            } catch {
                // unreadable sample: fall back to the real status
            }
        }
        return this.paymentStatus;
    }

    /**
     * Owed only while the server's PaymentReminder announcement is due (ZEY-3181). Before the current user has
     * loaded there is none, so nothing opens on a guess and then shuts when the server answers.
     */
    private ackDue(): boolean {
        return !!this.reminder();
    }

    private acknowledge(): void {
        const reminder = this.reminder();
        if (!reminder) return;
        // The server holds it back for the billing cadence; if the call fails it is still due and shows again on the
        // next reload or refresh.
        this.announcements.acknowledge(reminder).catch(() => this.announcements.drop(reminder.key));
    }

    private countdownSeconds(): number {
        return Math.max(0, Math.ceil(this.reminder()?.minDisplaySeconds ?? 0));
    }

    private open(mode: Exclude<PaymentPopupMode, 'none'>): void {
        this.stopCountdown();
        this.mode = mode;
        this.portalError = false;
        this.message = mode === 'reminder' ? reminderMessage(this.status(), new Date(this.now())) : '';
        if (mode === 'reminder') {
            this.checkStatementAccess();
            const countdownSeconds = this.countdownSeconds();
            this.secondsLeft = countdownSeconds;
            if (countdownSeconds > 0) {
                this.countdownSub = timer(1000, 1000)
                    .pipe(take(countdownSeconds))
                    .subscribe(() => {
                        this.secondsLeft = Math.max(0, this.secondsLeft - 1);
                        // ZEY-3071: closes itself (and acknowledges on the server) when the countdown runs out.
                        if (this.secondsLeft === 0) this.onContinue();
                        this.cdr.markForCheck();
                    });
            }
        } else {
            this.secondsLeft = 0;
        }
        this.document.addEventListener('keydown', this.onKeydownCapture, true);
        this.document.addEventListener('focusin', this.onFocusInCapture, true);
        this.needsFocus = true;
        this.cdr.markForCheck();
    }

    /**
     * Same menu data the route guard reads; only asked once the reminder is on screen. Hidden when the menu
     * didn't load: the link is only offered to users we know have the page.
     */
    private checkStatementAccess(): void {
        this.permissions
            .hasMenuAccess(STATEMENT_URL.slice(1))
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe((allowed) => {
                this.canViewStatement = allowed;
                this.cdr.markForCheck();
            });
    }

    private close(): void {
        this.stopCountdown();
        this.mode = 'none';
        this.secondsLeft = 0;
        this.needsFocus = false;
        this.document.removeEventListener('keydown', this.onKeydownCapture, true);
        this.document.removeEventListener('focusin', this.onFocusInCapture, true);
        this.releaseInert();
        this.cdr.markForCheck();
    }

    private stopCountdown(): void {
        this.countdownSub?.unsubscribe();
        this.countdownSub = undefined;
    }

    /**
     * Moves the overlay to the end of <body> and marks every other body child `inert` (the shell,
     * its micro-UI iframes, Kendo dialogs/popups, third-party widgets). Being the last body child
     * also wins z-index ties against widgets that use the maximum z-index too. Children added
     * while the popup is open (late widgets, new Kendo popups) are made inert and the overlay is
     * moved back on top. Angular removes the overlay node itself when the popup closes.
     */
    private portalToBody(overlay: HTMLElement): void {
        const body = this.document.body;
        if (body.lastElementChild !== overlay) body.appendChild(overlay);
        this.releaseInert();
        for (const child of Array.from(body.children)) this.makeInert(child, overlay);

        this.bodyObserver?.disconnect();
        if (typeof MutationObserver === 'undefined') return;
        this.bodyObserver = new MutationObserver((records) => {
            let added = false;
            for (const r of records) {
                r.addedNodes.forEach((n) => {
                    if (n !== overlay && n instanceof HTMLElement) {
                        this.makeInert(n, overlay);
                        added = true;
                    }
                });
            }
            if (added && body.lastElementChild !== overlay) {
                body.appendChild(overlay);
                this.focusDialog();
            }
        });
        this.bodyObserver.observe(body, { childList: true });
    }

    private makeInert(el: Element, overlay: HTMLElement): void {
        if (el === overlay || !(el instanceof HTMLElement)) return;
        if (el.hasAttribute('inert') || ['SCRIPT', 'STYLE', 'LINK', 'TEMPLATE'].includes(el.tagName)) return;
        el.setAttribute('inert', '');
        this.inerted.push(el);
    }

    private focusDialog(): void {
        const dialog = this.dialogRef?.nativeElement;
        if (!dialog) return;
        const target = dialog.querySelector<HTMLElement>('.pp-btn--primary:not([disabled])') ?? dialog;
        target.focus();
    }

    private releaseInert(): void {
        this.bodyObserver?.disconnect();
        this.bodyObserver = undefined;
        this.inerted.forEach((el) => el.removeAttribute('inert'));
        this.inerted = [];
    }

    private trapKeydown(e: KeyboardEvent): void {
        if (this.mode === 'none') return;
        const dialog = this.dialogRef?.nativeElement;
        if (e.key === 'Escape' || e.key === 'Esc') {
            e.preventDefault();
            e.stopImmediatePropagation();
            return;
        }
        if (e.key === 'Tab' && dialog) {
            const focusables = Array.from(
                dialog.querySelectorAll<HTMLElement>('button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'),
            );
            e.preventDefault();
            e.stopImmediatePropagation();
            if (focusables.length === 0) {
                dialog.focus();
                return;
            }
            const idx = focusables.indexOf(this.document.activeElement as HTMLElement);
            const next = e.shiftKey
                ? (idx <= 0 ? focusables.length - 1 : idx - 1)
                : (idx === -1 || idx === focusables.length - 1 ? 0 : idx + 1);
            focusables[next].focus();
            return;
        }
        // Keys aimed at anything outside the dialog (shortcuts, typing into the page) are swallowed.
        if (dialog && !dialog.contains(e.target as Node)) {
            e.preventDefault();
            e.stopImmediatePropagation();
        }
    }

    private trapFocus(e: FocusEvent): void {
        const dialog = this.dialogRef?.nativeElement;
        if (this.mode === 'none' || !dialog) return;
        if (!dialog.contains(e.target as Node)) dialog.focus();
    }
}
